import { cloneElement, useId } from "react";
import type { ReactElement, ReactNode } from "react";

export type FieldProps = {
  label: string;
  hint?: string | null;
  error?: string | null;
  required?: boolean;
  inline?: boolean;
  className?: string;
  children: ReactElement<Record<string, unknown>> | ReactNode;
};

export function Field({ label, hint = null, error = null, required = false, inline = false, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint !== null ? hintId : null, error !== null ? errorId : null].filter((a): a is string => a !== null).join(" ") || undefined;
  const control =
    children !== null && typeof children === "object" && "props" in children && typeof (children as ReactElement).type !== "undefined"
      ? cloneElement(children as ReactElement<Record<string, unknown>>, { id, "aria-describedby": describedBy, "aria-invalid": error !== null || undefined, "aria-required": required || undefined })
      : children;

  return (
    <div className={["fxmap-field", inline ? "fxmap-field-inline" : "", error !== null ? "fxmap-field-invalid" : "", className ?? ""].filter(Boolean).join(" ")}>
      <label className="fxmap-field-label" htmlFor={id}>
        {label}
        {required ? <span className="fxmap-field-required" aria-hidden="true">*</span> : null}
      </label>
      <div className="fxmap-field-control">{control}</div>
      {hint !== null ? (
        <p id={hintId} className="fxmap-field-hint fxmap-muted">
          {hint}
        </p>
      ) : null}
      {error !== null ? (
        <p id={errorId} className="fxmap-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
